import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Search,
  Plus,
  FileText,
  Loader2,
  XCircle,
  Pencil,
  CheckCircle2,
  PlayCircle,
  Send,
} from 'lucide-react'
import { CampaignCard } from './campaign-card'
import {
  useWACampaigns,
  useWACampaignStats,
  useDeleteWACampaign,
  useExecuteWACampaign,
  useCancelWACampaign,
} from '@/hooks/useWACampaigns'
import type { WACampaign, WACampaignStatus, WACampaignsListParams } from '@/lib/api-client'

export function CampaignList() {
  const navigate = useNavigate()

  // State
  const [searchQuery, setSearchQuery] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<WACampaignStatus | 'all'>('all')
  const [page, setPage] = React.useState(1)

  // Confirmation dialogs
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false)
  const [executeConfirmOpen, setExecuteConfirmOpen] = React.useState(false)
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false)
  const [targetCampaign, setTargetCampaign] = React.useState<WACampaign | null>(null)

  // Build query params
  const params: WACampaignsListParams = {
    page,
    limit: 12,
    search: searchQuery || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  }

  // Queries
  const { data, isPending, isError, refetch } = useWACampaigns(params)
  const { data: stats } = useWACampaignStats()

  // Mutations
  const deleteMutation = useDeleteWACampaign()
  const executeMutation = useExecuteWACampaign()
  const cancelMutation = useCancelWACampaign()

  const campaigns = data?.data || []
  const pagination = data?.pagination

  // Handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setPage(1)
  }

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as WACampaignStatus | 'all')
    setPage(1)
  }

  const handleView = (campaign: WACampaign) => {
    navigate({ to: '/whatsapp-campaigns/$id', params: { id: String(campaign.id) } })
  }

  const handleEdit = (campaign: WACampaign) => {
    if (campaign.status === 'draft') {
      navigate({ to: '/whatsapp-campaigns/new', search: { editId: campaign.id } })
    } else {
      handleView(campaign)
    }
  }

  const handleDeleteClick = (campaign: WACampaign) => {
    setTargetCampaign(campaign)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (targetCampaign) {
      deleteMutation.mutate(targetCampaign.id, {
        onSuccess: () => {
          setDeleteConfirmOpen(false)
          setTargetCampaign(null)
        },
      })
    }
  }

  const handleExecuteClick = (campaign: WACampaign) => {
    setTargetCampaign(campaign)
    setExecuteConfirmOpen(true)
  }

  const handleExecuteConfirm = () => {
    if (targetCampaign) {
      executeMutation.mutate({ id: targetCampaign.id }, {
        onSuccess: () => {
          setExecuteConfirmOpen(false)
          setTargetCampaign(null)
        },
      })
    }
  }

  const handleCancelClick = (campaign: WACampaign) => {
    setTargetCampaign(campaign)
    setCancelConfirmOpen(true)
  }

  const handleCancelConfirm = () => {
    if (targetCampaign) {
      cancelMutation.mutate(targetCampaign.id, {
        onSuccess: () => {
          setCancelConfirmOpen(false)
          setTargetCampaign(null)
        },
      })
    }
  }

  const handleCreateCampaign = () => {
    navigate({ to: '/whatsapp-campaigns/new', search: { editId: undefined } })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar - consistent with other pages */}
      <div className="flex-shrink-0 p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4 border-b border-border bg-page">
        {/* Filters Row - consistent with Patients page */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="relative w-full sm:w-auto sm:min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-9 h-10 text-sm bg-card border border-border rounded-lg"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="h-10 text-sm bg-card border border-border min-w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in_progress">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Button
              onClick={handleCreateCampaign}
              variant="default"
              size="default"
              className="h-10 bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Create Campaign</span>
              <span className="sm:hidden">Create</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {isPending ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <CampaignCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center py-12">
            <XCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-sm text-text-muted mb-4">Failed to load campaigns</p>
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Send className="w-12 h-12 text-text-muted mb-4" />
            <p className="text-sm text-text-muted mb-2">No campaigns found</p>
            <p className="text-xs text-text-muted mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'Try changing filters or search'
                : 'Create your first campaign'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Button onClick={handleCreateCampaign}>
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onView={() => handleView(campaign)}
                  onEdit={() => handleEdit(campaign)}
                  onDelete={() => handleDeleteClick(campaign)}
                  onExecute={() =>
                    campaign.status === 'in_progress'
                      ? handleCancelClick(campaign)
                      : handleExecuteClick(campaign)
                  }
                  onViewAnalytics={() => handleView(campaign)}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-text-muted px-4">
                  Page {page} / {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete campaign "{targetCampaign?.name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Execute Confirmation */}
      <AlertDialog open={executeConfirmOpen} onOpenChange={setExecuteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Execute Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to send messages to{' '}
              <strong>{targetCampaign?.totalRecipients.toLocaleString()}</strong> recipients?
              This process will start immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExecuteConfirm}
              className="bg-primary"
            >
              {executeMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel campaign "{targetCampaign?.name}"?
              Messages already sent will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Cancel Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Stat Card component - consistent with Analytics page style
function StatCard({
  icon,
  label,
  value,
  variant,
}: {
  icon: React.ReactNode
  label: string
  value: number
  variant?: 'info' | 'success' | 'destructive'
}) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
      <div
        className={cn(
          'flex items-center justify-center w-10 h-10 rounded-lg',
          variant === 'info' && 'bg-info/10',
          variant === 'success' && 'bg-success/10',
          variant === 'destructive' && 'bg-destructive/10',
          !variant && 'bg-primary/10'
        )}
      >
        {icon}
      </div>
      <div>
        <p className="text-2xl font-semibold text-text-dark">{value}</p>
        <p className="text-sm text-text-muted">{label}</p>
      </div>
    </div>
  )
}

// Skeleton for loading state
function CampaignCardSkeleton() {
  return (
    <div className="flex flex-col p-4 rounded-lg border border-border bg-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1">
          <Skeleton className="h-4 w-32 mb-2" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <Skeleton className="h-8 w-8 rounded" />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-border/50">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  )
}
